import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  MEASURE_UNITS,
  TIME_RANGES,
} from "../../constants/placeholders";
import {
  getMeasureDefinition, getMeasurePayloadKeys, getMeasureUnit,
} from "../../config/sensors.config";

dayjs.extend(utc);

export function getMeasuresLabel(sensors) {
  const measureList = [];

  (Array.isArray(sensors) ? sensors : []).forEach((sensor) => {
    (sensor?.sensor_type?.measured_parameters || []).forEach((measure) => {
      const definition = getMeasureDefinition(measure, sensor);
      measureList.push({
        label: `${sensor.nickname} - ${definition.label}`,
        value: `${sensor.id}_${definition.apiKey}`,
        unit: definition.unit,
      });
    });
  });

  return measureList;
}

export function parseMeasureId(measureString) {
  if (!measureString || typeof measureString !== "string") {
    return { sensorId: null, measure: null };
  }
  const lastUnderscoreIndex = measureString?.indexOf("_");

  if (lastUnderscoreIndex === -1) {
    return {
      sensorId: null,
      measure: measureString,
    };
  }

  return {
    sensorId: measureString?.substring(0, lastUnderscoreIndex),
    measure: measureString?.substring(lastUnderscoreIndex + 1),
  };
}

export function calculateTimeRange(selectedButton, now = dayjs().utc()) {
  const range = TIME_RANGES[selectedButton];

  if (!range) {
    return now;
  }

  return now.subtract(range.value, range.unit);
}

export function formatToISO(date) {
  return date.utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
}

export function getCustomDateRange(customDateRange) {
  return {
    from: dayjs(customDateRange[0]),
    to: dayjs(customDateRange[1]),
  };
}

export const formatChartData = (chartData) => {
  if (
    !chartData ||
    typeof chartData !== "object" ||
    Object.keys(chartData).length === 0
  ) {
    return [];
  }

  const dataByTimestamp = new Map();
  const allMeasureKeys = Object.keys(chartData);

  Object.entries(chartData).forEach(([key, values]) => {
    if (!Array.isArray(values)) return;

    values.forEach((item) => {
      const timestamp = item.Timestamp;

      if (!timestamp) return;

      if (!dataByTimestamp.has(timestamp)) {
        const dataPoint = { timestamp };
        allMeasureKeys.forEach((measureKey) => {
          dataPoint[measureKey] = null;
        });
        dataByTimestamp.set(timestamp, dataPoint);
      }

      const valueKey = Object.keys(item).find((k) => k !== "Timestamp");
      const value = valueKey ? item[valueKey] : null;

      dataByTimestamp.get(timestamp)[key] = value;
    });
  });

  return Array.from(dataByTimestamp.values()).sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );
};

export const hasSelectedChartData = (data, selectedMeasures = []) =>
  (data || []).some((row) =>
    selectedMeasures.some((measure) => Number.isFinite(row[measure])),
  );

export const projectMeasureData = (rows, measure, isAverage = false, responseMeasure = measure) => {
  if (!Array.isArray(rows)) throw new Error("Risposta dei dati del grafico non valida");
  const payloadKeys = Array.from(new Set([
    ...getMeasurePayloadKeys(responseMeasure),
    ...getMeasurePayloadKeys(measure),
  ]));
  const keys = isAverage
    ? [...payloadKeys.map((key) => `${key}_mean`), ...payloadKeys]
    : payloadKeys;
  return rows.map((row) => ({
    Timestamp: row.Timestamp,
    [measure]: keys.map((key) => row[key]).find(Number.isFinite) ?? null,
  }));
};

export const getChartErrorMessage = (error) => {
  const detail = error?.error || error;
  if (Number(detail?.code) >= 500 && Number(detail?.code) < 600) {
    return "Il servizio non riesce a caricare i dati per il periodo selezionato. Riprova più tardi.";
  }
  if (detail?.localized_error === "invalid_dataframe") {
    return "Dati non disponibili per alcune misure nel periodo selezionato.";
  }
  if (detail?.localized_error === "no_sensor_founded") {
    return "Nessun sensore disponibile per le misure selezionate.";
  }
  return detail?.message ||
    (typeof error === "string" ? error : "Impossibile caricare i dati del grafico");
};

export const getFormattedValue = (value, measureKey = "", explicitUnit) => {
  if (!Number.isFinite(value)) return "N/A";

  const measureType = measureKey.includes(" - ")
    ? measureKey.split(" - ")[1]
    : measureKey;

  const unit = explicitUnit ?? (getMeasureUnit(measureKey) || MEASURE_UNITS[measureType] || "");
  return unit ? `${value.toFixed(2)} ${unit}` : `${value.toFixed(2)}`;
};
